import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import { store } from '../stores/store';
import { shouldEndMerchantSession, withMerchantAuthorization } from './merchantAuthorization';
import { isCurrentShopifyRequest, shouldRetryShopifyRequest, ShopifyRequestConfig, withShopifySessionAuthorization } from './shopifySessionAuthorization';
import { User, UserFormValues } from '../models/User';
import { SupportTicketRequest, SupportTicketResult } from '../models/SupportTicket';
import { ShopifyAuth } from '../models/ShopifyAuth';
import { UserSetting } from '../models/UserSetting';
import { StorefrontThemeSettings } from '../models/StorefrontThemeSettings';
import { StorefrontResultsSettings } from '../models/StorefrontResultsSettings';
import { StorefrontTerminologyPresetUpdate, StorefrontTranslations, StorefrontTranslationsUpdate } from '../models/StorefrontTranslations';
import { InventoryLocationOption } from '../models/InventoryLocation';
import { FitmentCatalogSettings } from '../models/FitmentCatalog';
import { Turn14ConnectionTestResult, Turn14IntegrationSettings } from '../models/Turn14Integration';
import { VendorSkuRule } from '../models/VendorSkuRule';
import { ShopifySyncLog } from '../models/ShopifySyncLog';
import { DataSyncStatus } from '../models/DataSync';
import { DashboardSettings } from '../models/DashboardSettings';
import { SetupHealth } from '../models/SetupHealth';
import { Group, GroupFormValues } from '../models/Group';
import { ProductConfiguratorAssignmentState, ProductGuideCollectionOption, ProductGuideDetail, ProductGuideResolveRequest, ProductGuideResolveResponse, ProductGuideSummary } from '../models/ProductGuide';
import { ServiceBookingAppointment, ServiceBookingAppointmentList, ServiceBookingCalendar, ServiceBookingPaymentReconciliation, ServiceBookingSettings, ServiceBookingStatus } from '../models/ServiceBooking';
import { ShopifyMetafieldAttributeOption, ShopifyMetafieldAttributeUpsert, ShopifyMetafieldDefinitionOption } from '../models/ShopifyMetafieldAttribute';
import { PricePullHistory, Pricing } from '../models/Pricing';
import { ProductDetails, ProductFitmentCopyJob, ProductFitmentCopySource, ProductFitmentPage, ProductListResult, ProductMappingStatus, RealtimeProductListResult, SaveFitmentChangesPayload, VariantFitmentReview } from '../models/Product';
import type { CollectionMappingDetails, CollectionMappingListResult, SaveCollectionMappingPayload, SelectedCollectionMapping } from '../models/CollectionMapping';
import { VehicleListResult, VehicleCreate, VehicleEdit, VehicleFilterOptionsResult } from '../models/Vehicle';
import { CoverageFilter, CoverageReportSettings, FitmentCoverageHealthReport, StorefrontSearchReport } from '../models/Reports';
import {
    LegacyAccount,
    LegacyAdminSettings,
    LegacyOperationHistory,
    LegacyOperationResult,
    LegacyProductMappingPage,
    LegacyProductMappingSave,
    LegacyProductMappingSaveResult,
    LegacyStorefrontStatus,
    LegacyVehicleLevel,
    LegacyVehicleMutation,
} from '../models/LegacyMode';
import { AdminActivityDashboard, AdminAtdConnectionTestResult, AdminAtdCreateTestProductResponse, AdminAtdFilteredProductSample, AdminAtdProfile, AdminDataFeedInventoryDiagnosticsResponse, AdminDataFeedProfile, AdminDataFeedRunLog, AdminDataFeedStoreDetails, AdminDataFeedStoreSummary, AdminDataFeedVendorRule, AdminDataFeedVendorRuleImportResult, AdminFitmentImportLog, AdminLoginResponse, AdminPagedResult, AdminShopRuntime, AdminShopifyAppInstallMapping, AdminStoreDetails, AdminStoreGeneralSettings, AdminStoreNote, AdminStoreShopifyAppCredentials, AdminStoreSummary, AdminStoreSyncError, AdminStoreSyncLog, AdminSyncHealthDashboard, AdminSyncLogErrorsResponse, AdminWebhookAudit, AdminWebhookLog, AdminTurn14ItemSummary, AdminTurn14ItemDetails, AdminTurn14SyncLog } from '../models/Admin';
import {
    AtdDiagnosticActionResult,
    AtdDiagnostics,
    AtdConnectionTestResult,
    AtdProductSyncCleanupRequest,
    AtdProductSyncCleanupResult,
    AtdProductDetail,
    AtdProductListResponse,
    AtdManualProductLinkRequest,
    AtdManualProductLinkResult,
    AtdProductShopifyLinkRequest,
    AtdProductSyncSummary,
    AtdPreviewDiagnosticRequest,
    AtdPreviewProductSearchItem,
    AtdProfile,
    AtdProfileFilterOptions,
    AtdProductLookupDiagnosticRequest,
    AtdShipToHomeCancelEligibilityResult,
    AtdShipToHomeOrderDetail,
    AtdShipToHomeOrderLinkTarget,
    AtdShipToHomeOrderListResponse,
    AtdShipToHomeProcessResult,
    AtdShipToHomeStatusSyncResult,
    AtdCheckoutCarrierServiceStatus,
    AtdShopifyProductSearchItem,
    AtdStatusLookupDiagnosticRequest,
} from '../models/Atd';

//axios.defaults.baseURL = 'http://localhost:5000/api';
//axios.defaults.baseURL = 'https://rh.ngrok.app/api';
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const adminTokenStorageKey = "partfinder.admin.token";
const previewTokenStorageKey = "partfinder.preview.token";

// Handoff is a one-time authentication exchange, not an existing merchant request.
// Keep stale bearer tokens, shared logout hooks and automatic retries out of it.
const legacyPilotHandoffClient = axios.create({ baseURL: process.env.REACT_APP_API_URL, timeout: 15000 });

// this sends bearer token (if available) with each request
axios.interceptors.request.use(async config => {
    const shopifyConfig = config as ShopifyRequestConfig;
    if (shopifyConfig.shopifySessionRevision !== undefined && !isCurrentShopifyRequest(shopifyConfig, store.commonStore)) {
        throw new Error('The merchant session changed before this request could be retried.');
    }
    // Login has its own verified payload; console/preview calls provide their
    // own Authorization header and must never inherit merchant credentials.
    if (/^\/pf-(console|preview)(\/|$)/.test(config.url || '')) return config;
    if (['/account/login', '/account/embeddedlogin', '/account/auth', '/account/embeddedauth'].includes(config.url || '')) return config;
    if (store.commonStore.shopifySession) {
        return withShopifySessionAuthorization(config, store.commonStore, async () => {
            if (!window.shopify?.idToken) throw new Error('Shopify session unavailable. Reopen the app from Shopify admin.');
            return window.shopify.idToken();
        }, process.env.REACT_APP_API_URL || '/api');
    }
    return withMerchantAuthorization(config, store.commonStore.token);
})

// Used to create fake delay, for testing loading states
const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    })
}

// Used to create fake delay, for testing loading states
axios.interceptors.response.use(async response => {
    try {
        const requestUrl = response.config?.url ?? "";
        const refreshedToken = response.headers?.["x-session-token"];
        if (typeof refreshedToken === "string" && refreshedToken.trim().length > 0) {
            if (requestUrl.startsWith("/pf-console")) {
                localStorage.setItem(adminTokenStorageKey, refreshedToken);
            } else if (requestUrl.startsWith("/pf-preview")) {
                localStorage.setItem(previewTokenStorageKey, refreshedToken);
            }
        }

        //await sleep(500);
        return response;
    } catch (error) {
        console.log(error);
        return await Promise.reject(error);
    }
}, async error => {
    const { status } = error.response || {};
    const config = error.config as ShopifyRequestConfig | undefined;
    if (shouldRetryShopifyRequest(status, error.response?.headers?.['x-shopify-retry-invalid-session-request'], config, store.commonStore)) {
        config!.shopifySessionRetried = true;
        return axios(config!);
    }
    if ((status === 401 && isCurrentShopifyRequest(config, store.commonStore))
        || shouldEndMerchantSession(status, config, store.commonStore.token)) {
        store.userStore?.logout?.(true);
    }
    return Promise.reject(error);
});

const responseBody = <T> (response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T> (url: string, signal?: AbortSignal) => axios.get<T>(url, { signal }).then(responseBody),
    post: <T> (url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T> (url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T> (url: string) => axios.delete<T>(url).then(responseBody),
}

const appendShopOverride = (url: string, shopIdOverride?: string | number) => {
    if (shopIdOverride === undefined || shopIdOverride === null || shopIdOverride === "") {
        return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}shopIdOverride=${encodeURIComponent(shopIdOverride.toString())}`;
};

const appendFieldValues = (params: URLSearchParams, fieldValues?: Record<string, string>) => {
    // Keep the URL compact and predictable: only selected selector values are
    // serialized, and the server merges this map with the legacy Y/M/M params.
    const normalizedValues = Object.entries(fieldValues ?? {})
        .filter(([, value]) => typeof value === "string" && value.trim().length > 0)
        .reduce<Record<string, string>>((accumulator, [key, value]) => {
            accumulator[key] = value.trim();
            return accumulator;
        }, {});

    if (Object.keys(normalizedValues).length > 0) {
        params.set("fieldValues", JSON.stringify(normalizedValues));
    }
};

const appendFieldOrder = (params: URLSearchParams, fieldOrder?: string[]) => {
    const normalizedOrder = (fieldOrder ?? [])
        .map((key) => key.trim().toLowerCase())
        .filter((key, index, values) => key.length > 0 && values.indexOf(key) === index);

    if (normalizedOrder.length > 0) {
        params.set("fieldOrder", JSON.stringify(normalizedOrder));
    }
};

const Account = {
    resolveOriginalApp: (shop: string, idToken: string) => legacyPilotHandoffClient.post<{ owner: string; legacyUrl?: string }>('/original-app/resolve', { shop, idToken }).then(responseBody),
    endImpersonation: () => requests.post<void>('/operator-access/end', {}),
    redeemOperatorSession: (code: string) => legacyPilotHandoffClient.post<User>('/operator-access/redeem', { code }).then(responseBody),
    redeemLegacyPilot: (code: string) => legacyPilotHandoffClient.post<User>('/legacy-pilot/redeem', { code }).then(responseBody),
    auth: (user: UserFormValues) => requests.post<string>('/account/auth', user),
    embeddedauth: (user: ShopifyAuth) => requests.post<string>('/account/embeddedauth', user),
    embeddedlogin: (user: ShopifyAuth) => requests.post<User>('/account/embeddedlogin', user),
    login: (user: ShopifyAuth) => axios.post<User>('/account/login', user, { withCredentials: true }).then(responseBody),
    current: () => requests.get<User>('/account'),
    getSubscriptionStatus: () => requests.get<{ hasActiveSubscription: boolean }>('/account/subscription/status'),

}

const Settings = {
    details: (shopIdOverride?: string | number) => requests.get<UserSetting>(appendShopOverride('/settings', shopIdOverride)),
    update: (setting: UserSetting, shopIdOverride?: string | number) => axios.put<void>(appendShopOverride('/settings', shopIdOverride), setting),
    theme: (shopIdOverride?: string | number) => requests.get<StorefrontThemeSettings>(appendShopOverride('/settings/theme', shopIdOverride)),
    updateTheme: (setting: StorefrontThemeSettings, shopIdOverride?: string | number) => requests.put<StorefrontThemeSettings>(appendShopOverride('/settings/theme', shopIdOverride), setting),
    inventoryLocations: (shopIdOverride?: string | number) => requests.get<InventoryLocationOption[]>(appendShopOverride('/settings/inventory/locations', shopIdOverride)),
    vendorSkuRules: (shopIdOverride?: string | number) => requests.get<VendorSkuRule[]>(appendShopOverride('/settings/vendor-sku-rules', shopIdOverride)),
    updateVendorSkuRules: (rules: VendorSkuRule[], shopIdOverride?: string | number) => requests.put<VendorSkuRule[]>(appendShopOverride('/settings/vendor-sku-rules', shopIdOverride), rules),
    fitmentCatalog: (shopIdOverride?: string | number) => requests.get<FitmentCatalogSettings>(appendShopOverride('/settings/fitment-catalog', shopIdOverride)),
    updateFitmentCatalog: (catalog: FitmentCatalogSettings, shopIdOverride?: string | number) => requests.put<FitmentCatalogSettings>(appendShopOverride('/settings/fitment-catalog', shopIdOverride), catalog),
    turn14Integration: (shopIdOverride?: string | number) => requests.get<Turn14IntegrationSettings>(appendShopOverride('/settings/integrations/turn14', shopIdOverride)),
    updateTurn14Integration: (settings: Turn14IntegrationSettings, shopIdOverride?: string | number) =>
        requests.put<Turn14IntegrationSettings>(appendShopOverride('/settings/integrations/turn14', shopIdOverride), settings),
    testTurn14Integration: (settings: Turn14IntegrationSettings, shopIdOverride?: string | number) =>
        requests.post<Turn14ConnectionTestResult>(appendShopOverride('/settings/integrations/turn14/test', shopIdOverride), settings),
    storefrontResults: (shopIdOverride?: string | number) => requests.get<StorefrontResultsSettings>(appendShopOverride('/settings/storefront-results', shopIdOverride)),
    themeAppearanceAudit: () => requests.get<{themeId:string;themeName:string;profiles:import('../models/StorefrontResultsSettings').ThemeAppearanceProfile[]}>('/settings/theme-appearance-audit'),
    updateStorefrontResults: (settings: StorefrontResultsSettings, shopIdOverride?: string | number) => requests.put<StorefrontResultsSettings>(appendShopOverride('/settings/storefront-results', shopIdOverride), settings),
    // Storefront translation editor endpoints. Locale is optional on load so the API can choose the shop default.
    storefrontTranslations: (locale?: string, shopIdOverride?: string | number) => {
        const params = new URLSearchParams();
        if (locale) params.set('locale', locale);
        const path = `/settings/storefront-translations${params.toString() ? `?${params.toString()}` : ''}`;
        return requests.get<StorefrontTranslations>(appendShopOverride(path, shopIdOverride));
    },
    updateStorefrontTranslations: (locale: string, payload: StorefrontTranslationsUpdate, shopIdOverride?: string | number) =>
        requests.put<StorefrontTranslations>(appendShopOverride(`/settings/storefront-translations/${encodeURIComponent(locale)}`, shopIdOverride), payload),
    previewStorefrontTerminologyPreset: (payload: StorefrontTerminologyPresetUpdate, shopIdOverride?: string | number) =>
        requests.post<StorefrontTranslations>(appendShopOverride('/settings/storefront-terminology-preset-preview', shopIdOverride), payload),
    updateStorefrontTerminologyPreset: (payload: StorefrontTerminologyPresetUpdate, shopIdOverride?: string | number) =>
        requests.put<StorefrontTranslations>(appendShopOverride('/settings/storefront-terminology-preset', shopIdOverride), payload),
    dataSyncStatus: (shopIdOverride?: string | number) =>
        requests.get<DataSyncStatus>(appendShopOverride('/settings/data-sync', shopIdOverride)),
    refreshCatalogData: (shopIdOverride?: string | number) =>
        requests.post<DataSyncStatus>(appendShopOverride('/settings/data-sync/refresh-catalog', shopIdOverride), {}),
    refreshAttributes: (shopIdOverride?: string | number) =>
        requests.post<DataSyncStatus>(appendShopOverride('/settings/data-sync/refresh-attributes', shopIdOverride), {}),
    dashboard: (shopIdOverride?: string | number) =>
        requests.get<DashboardSettings>(appendShopOverride('/settings/dashboard', shopIdOverride)),
    setupHealth: (themeId?: string, shopIdOverride?: string | number) => {
        const params = new URLSearchParams();
        if (themeId) params.set('themeId', themeId);
        const path = `/settings/setup-health${params.toString() ? `?${params.toString()}` : ''}`;
        return requests.get<SetupHealth>(appendShopOverride(path, shopIdOverride));
    },
    dismissVcdbReferenceCallout: (shopIdOverride?: string | number) =>
        requests.post<DashboardSettings>(appendShopOverride('/settings/dashboard/vcdb-reference-callout/dismiss', shopIdOverride), {}),
    dismissFeatureAnnouncement: (announcementId: string, shopIdOverride?: string | number) =>
        requests.post<DashboardSettings>(
            appendShopOverride(`/settings/dashboard/feature-announcements/${encodeURIComponent(announcementId)}/dismiss`, shopIdOverride),
            {}
        ),
    legacyStatus: (shopIdOverride?: string | number) =>
        requests.get<LegacyStorefrontStatus>(appendShopOverride('/settings/legacy-status', shopIdOverride)),
}

const LegacyAdmin = {
    settings: () => requests.get<LegacyAdminSettings>('/legacyadmin/settings'),
    updateSettings: (settings: LegacyAdminSettings) => requests.put<LegacyAdminSettings>('/legacyadmin/settings', settings),
    vehicles: (options: { level: string; make?: string; model?: string; submodel?: string }) => {
        const params = new URLSearchParams({ level: options.level });
        if (options.make) params.set('make', options.make);
        if (options.model) params.set('model', options.model);
        if (options.submodel) params.set('submodel', options.submodel);
        return requests.get<LegacyVehicleLevel>(`/legacyadmin/vehicles?${params.toString()}`);
    },
    addVehicle: (input: LegacyVehicleMutation) => requests.post<{ added: number }>('/legacyadmin/vehicles', input),
    updateVehicle: (input: LegacyVehicleMutation) => requests.put<void>('/legacyadmin/vehicles', input),
    deleteVehicle: (input: LegacyVehicleMutation) => {
        const params = new URLSearchParams({ level: input.level, value: input.value });
        if (input.make) params.set('make', input.make);
        if (input.model) params.set('model', input.model);
        if (input.submodel) params.set('submodel', input.submodel);
        return requests.del<{ deleted: number }>(`/legacyadmin/vehicles?${params.toString()}`);
    },
    mappingProducts: (options: {
        make: string;
        model: string;
        submodel?: string;
        year: number;
        query?: string;
        mappedOnly?: boolean;
        page?: number;
        pageSize?: number;
    }) => {
        const params = new URLSearchParams({
            make: options.make,
            model: options.model,
            year: String(options.year),
            page: String(options.page ?? 1),
            pageSize: String(options.pageSize ?? 50),
        });
        if (options.submodel) params.set('submodel', options.submodel);
        if (options.query) params.set('query', options.query);
        if (options.mappedOnly) params.set('mappedOnly', 'true');
        return requests.get<LegacyProductMappingPage>(`/legacyadmin/mapping/products?${params.toString()}`);
    },
    saveMappings: (input: LegacyProductMappingSave) =>
        requests.put<LegacyProductMappingSaveResult>('/legacyadmin/mapping/products', input),
    refreshUniversal: () => requests.post<LegacyOperationResult>('/legacyadmin/universal/refresh', {}),
    operations: () => requests.get<LegacyOperationHistory[]>('/legacyadmin/operations'),
    account: () => requests.get<LegacyAccount>('/legacyadmin/account'),
    deleteAllVehicles: () => requests.del<{ deleted: number }>('/legacyadmin/account/vehicles'),
}

const SyncLogInfo = {
    details: () => requests.get<ShopifySyncLog>('/synclog/details'),
    list: () => requests.get<ShopifySyncLog[]>('/synclog'),  
}


const PricingInfo = {
    details: () => requests.get<Pricing>('/pricing'),
    restore: (pricing: PricePullHistory) => axios.post<PricePullHistory>(`/pricing/`, pricing),
}

const Groups = {
    list: () => requests.get<Group[]>('/groups'),
    details: (id: string) => requests.get<Group>(`/groups/${id}`),
    create: (group: GroupFormValues) => axios.post<void>('/groups', group),
    update: (group: GroupFormValues) => axios.put<void>(`/groups/${group.id}`, group),
    delete: (id: string) => axios.delete<void>(`/groups/${id}`)
}

// Product guide admin endpoints. The API enforces shop ownership and the Trail Ops staging rollout gate.
const ProductGuides = {
    list: () => requests.get<ProductGuideSummary[]>('/productguides'),
    details: (id: number) => requests.get<ProductGuideDetail>(`/productguides/${id}`),
    collections: () => requests.get<ProductGuideCollectionOption[]>('/productguides/collections'),
    configuratorAssignment: (productId: number) => requests.get<ProductConfiguratorAssignmentState>(`/productguides/configurator-assignment/${productId}`),
    updateConfiguratorAssignment: (productId: number, productGuideId?: number | null) =>
        requests.put<ProductConfiguratorAssignmentState>(`/productguides/configurator-assignment/${productId}`, { productGuideId: productGuideId ?? null }),
    create: (guide: ProductGuideDetail) => requests.post<ProductGuideDetail>('/productguides', guide),
    update: (id: number, guide: ProductGuideDetail) => requests.put<ProductGuideDetail>(`/productguides/${id}`, guide),
    delete: (id: number) => requests.del<void>(`/productguides/${id}`),
    resolve: (id: number, request: ProductGuideResolveRequest) => requests.post<ProductGuideResolveResponse>(`/productguides/${id}/resolve`, request),
}

const ServiceBookings = {
    settings: () => requests.get<ServiceBookingSettings>('/servicebookings/settings'),
    updateSettings: (settings: ServiceBookingSettings) =>
        requests.put<ServiceBookingSettings>('/servicebookings/settings', settings),
    calendar: (date: string) =>
        requests.get<ServiceBookingCalendar>(`/servicebookings/calendar?date=${encodeURIComponent(date)}`),
    appointments: (status = '', search = '') => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (search) params.set('search', search);
        const query = params.toString();
        return requests.get<ServiceBookingAppointmentList>(`/servicebookings/appointments${query ? `?${query}` : ''}`);
    },
    reconcilePayments: () =>
        requests.post<ServiceBookingPaymentReconciliation>('/servicebookings/appointments/reconcile-payments', {}),
    updateStatus: (id: number, status: ServiceBookingStatus) =>
        requests.put<ServiceBookingAppointment>(`/servicebookings/appointments/${id}/status`, { status }),
}

const ShopifyMetafieldAttributes = {
    list: (options?: { enabledOnly?: boolean; includeValues?: boolean }) => {
        const params = new URLSearchParams();
        if (options?.enabledOnly !== undefined) {
            params.set('enabledOnly', String(options.enabledOnly));
        }
        if (options?.includeValues !== undefined) {
            params.set('includeValues', String(options.includeValues));
        }

        const query = params.toString();
        return requests.get<ShopifyMetafieldAttributeOption[]>(`/shopifymetafieldattributes${query ? `?${query}` : ''}`);
    },
    definitions: () =>
        requests.get<ShopifyMetafieldDefinitionOption[]>('/shopifymetafieldattributes/definitions'),
    upsert: (attribute: ShopifyMetafieldAttributeUpsert) =>
        requests.post<ShopifyMetafieldAttributeOption>('/shopifymetafieldattributes', attribute),
}

const Products = {
    list: (
        page: number,
        query: string = '',
        hasFitment: string = '',
        vendor: string = '',
        tag: string = '',
        sort: string = 'title asc',
        year: string = '',
        yearTo: string = '',
        make: string = '',
        model: string = '',
        mappedOnly: boolean = false,
        fieldValues?: Record<string, string>,
        status: string = '',
        signal?: AbortSignal,
        includeTotalCount: boolean = true,
    ) => {
        const params = new URLSearchParams({
            page: page.toString(),
            query,
            hasFitment,
            status,
            vendor,
            tag,
            sort,
            year,
            yearTo,
            make,
            model,
            mappedOnly: mappedOnly ? 'true' : 'false',
            includeTotalCount: String(includeTotalCount),
        });
        appendFieldValues(params, fieldValues);
        return requests.get<ProductListResult>(`/products?${params.toString()}`, signal);
    },
    // New endpoint that pulls product data directly from Shopify in real time.
    listRealtime: (query: string = '', cursor: string = '', pageSize: number = 50, sort: string = 'title asc', status: string = '', signal?: AbortSignal) => {
        const params = new URLSearchParams({
            query,
            cursor,
            pageSize: pageSize.toString(),
            sort,
            status,
        });
        return requests.get<RealtimeProductListResult>(`/products/realtime?${params.toString()}`, signal);
    },
    bulkFitments: (payload: any) => requests.post<void>('/products/bulk-fitments', payload),
    mappingStatus: (payload: { productIds: number[]; fieldValues: Record<string, string>; yearTo: string }, signal?: AbortSignal) =>
        axios.post<ProductMappingStatus>('/products/mapping-status', payload, { signal }).then(responseBody),
    details: (id: number, variantId?: number | null, fitmentPage?: number, fitmentPageSize?: number, editor = false, includeFitments = true, signal?: AbortSignal) => {
        const params = new URLSearchParams();
        if (variantId !== undefined && variantId !== null) {
            params.set('variantId', variantId.toString());
        }
        if (fitmentPage) {
            params.set('fitmentPage', fitmentPage.toString());
        }
        if (fitmentPageSize) {
            params.set('fitmentPageSize', fitmentPageSize.toString());
        }

        const queryString = params.toString();
        return axios.get<ProductDetails>(`/products/${id}?${queryString}&editor=${editor}&includeFitments=${includeFitments}`, { signal }).then(responseBody);
    },
    fitments: (id: number, variantId?: number | null, page: number = 1, pageSize: number = 100, search = '', grouped = false, signal?: AbortSignal) => {
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
        });
        if (variantId !== undefined && variantId !== null) {
            params.set('variantId', variantId.toString());
        }

        params.set('search', search);
        params.set('grouped', String(grouped));
        return axios.get<ProductFitmentPage>(`/products/${id}/fitments?${params.toString()}`, { signal }).then(responseBody);
    },
    saveFitmentChanges: (id: number, payload: SaveFitmentChangesPayload) =>
        requests.post<{ syncPending: boolean; message: string }>(`/products/${id}/fitment-changes`, payload),
    previewVariantFitmentChanges: (id: number, payload: SaveFitmentChangesPayload) =>
        requests.post<VariantFitmentReview>(`/products/${id}/fitment-changes/preview`, payload),
    fitmentEditSummary: (id: number, variantId?: number | null) => requests.get<{ count: number; version: string }>(`/products/${id}/fitment-edit-summary?variantId=${variantId ?? 0}`),
    pendingFitmentSave: (id: number) => requests.get<{ id: string | null }>(`/products/${id}/pending-fitment-save`),
    retryFitmentSave: (id: number, batchId: string) => requests.post<{ syncPending: boolean; message: string }>(`/products/${id}/pending-fitment-save/${batchId}/retry`, {}),
    addFitment: (id: number, payload: { year: string; make: string; model: string; submodel?: string; fieldValues?: Record<string, string>; notes?: string[] }) =>
        requests.post<void>(`/products/${id}/fitments`, payload),
    saveFitmentTags: (id: number, payload: { variantId?: number | null; vehicleTags: string[]; isUniversal: boolean; fitments?: Array<{ year: string; make: string; model: string; submodel?: string; fieldValues?: Record<string, string>; notes?: string[] }> }) =>
        requests.put<void>(`/products/${id}/fitments`, payload),
    removeFitment: (id: number, fitmentId: number) => requests.del<void>(`/products/${id}/fitments/${fitmentId}`),
    fitmentCopySource: (id: number) =>
        requests.get<ProductFitmentCopySource>(`/products/${id}/fitment-copy-source`),
    createFitmentCopyJob: (id: number, sourceProductId: number, targetVariantId: number | null, includeNotes: boolean) =>
        requests.post<ProductFitmentCopyJob>(`/products/${id}/fitment-copy-jobs`, { sourceProductId, targetVariantId, includeNotes }),
    listFitmentCopyJobs: (id: number, limit: number = 10, options?: { recentOnly?: boolean; beforeId?: number; signal?: AbortSignal }) => {
        const params = new URLSearchParams({ limit: String(limit) });
        if (options?.recentOnly) params.set('recentOnly', 'true');
        if (options?.beforeId) params.set('beforeId', String(options.beforeId));
        return requests.get<ProductFitmentCopyJob[]>(`/products/${id}/fitment-copy-jobs?${params}`, options?.signal);
    },
    listRecentFitmentCopyJobs: (activeOnly: boolean = false, limit: number = 10) =>
        requests.get<ProductFitmentCopyJob[]>(`/products/fitment-copy-jobs?activeOnly=${activeOnly ? 'true' : 'false'}&limit=${limit}`),
    retryFitmentCopyJob: (id: number, jobId: number) =>
        requests.post<ProductFitmentCopyJob>(`/products/${id}/fitment-copy-jobs/${jobId}/retry`, {})
}

const CollectionMappings = {
    list: (
        page: number,
        query: string,
        mappedOnly: boolean,
        year: string,
        yearTo: string,
        make: string,
        model: string,
        submodel: string,
        fieldValues?: Record<string, string>,
        sort: string = 'title asc',
        collectionType: string = '',
        signal?: AbortSignal
    ) => {
        const params = new URLSearchParams({
            page: page.toString(),
            query,
            mappedOnly: mappedOnly ? 'true' : 'false',
            sort,
            collectionType,
            year,
            yearTo,
            make,
            model,
            submodel,
        });
        appendFieldValues(params, fieldValues);
        return requests.get<CollectionMappingListResult>(`/collectionmappings?${params.toString()}`, signal);
    },
    saveMapping: (payload: SaveCollectionMappingPayload) =>
        requests.put<SelectedCollectionMapping>('/collectionmappings/mapping', payload),
    details: (collectionId: number, page: number = 1, query: string = '', pageSize: number = 50) => {
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            query,
        });
        return requests.get<CollectionMappingDetails>(`/collectionmappings/${collectionId}?${params.toString()}`);
    },
    removeMappedVehicle: (collectionId: number, mappingId: number) =>
        requests.del<void>(`/collectionmappings/${collectionId}/vehicles/${mappingId}`),
    removeMappedVehicles: (collectionId: number, mappingIds: number[]) =>
        requests.post<number>(`/collectionmappings/${collectionId}/vehicles/remove`, { mappingIds }),
};

const Vehicles = {
    list: (
        page: number,
        query: string = '',
        source: string = '',
        sort: string = 'fitment-fields asc',
        year: string = '',
        make: string = '',
        model: string = '',
        assigned: string = '',
        fieldValues?: Record<string, string>,
        view: 'ranges' | 'individual' = 'individual',
        signal?: AbortSignal,
        yearFrom: string = '',
        yearTo: string = ''
    ) => {
        const params = new URLSearchParams({
            page: page.toString(),
            query,
            source,
            sort,
            year,
            make,
            model,
            assigned,
            view,
        });
        if (yearFrom) params.set('yearFrom', yearFrom);
        if (yearTo) params.set('yearTo', yearTo);
        appendFieldValues(params, fieldValues);
        return axios.get<VehicleListResult>(`/vehicles?${params.toString()}`, { signal, timeout: 30000 }).then(responseBody);
    },
    filterOptions: (
        year: string = '',
        make: string = '',
        source: string = '',
        fieldValues?: Record<string, string>,
        fieldOrder?: string[],
        optionFields?: string[],
        signal?: AbortSignal
    ) => {
        const params = new URLSearchParams({ year, make, source });
        appendFieldValues(params, fieldValues);
        appendFieldOrder(params, fieldOrder);
        if (optionFields) params.set('optionFields', JSON.stringify(optionFields));
        return requests.get<VehicleFilterOptionsResult>(`/vehicles/filter-options?${params.toString()}`, signal);
    },
    create: (vehicle: VehicleCreate) => requests.post<void>('/vehicles', vehicle),
    update: (id: number, vehicle: VehicleEdit) => requests.put<void>(`/vehicles/${id}`, vehicle),
    delete: (id: number) => requests.del<void>(`/vehicles/${id}`),
    bulkDelete: (payload: any) => requests.post<void>('/vehicles/bulk-delete', payload),
}

const Atd = {
    // Merchant-facing Ship-To-Home operations. These endpoints are scoped on the
    // server to the authenticated user's Shopify shop.
    profile: () => requests.get<AtdProfile>('/atd/profile'),
    profileFilterOptions: () => requests.get<AtdProfileFilterOptions>('/atd/profile/filter-options'),
    saveProfile: (payload: AtdProfile) => requests.put<AtdProfile>('/atd/profile', payload),
    requestProductRepair: () => requests.post<AtdDiagnosticActionResult>('/atd/profile/request-product-repair', {}),
    testProfile: (payload: AtdProfile) => requests.post<AtdConnectionTestResult>('/atd/profile/test', payload),
    shipToHomeOrders: (page: number = 1, pageSize: number = 25, status: string = 'all', query: string = '', sort: string = 'created_desc') => {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
            status,
            query,
            sort,
        });

        return requests.get<AtdShipToHomeOrderListResponse>(`/atd/ship-to-home/orders?${params.toString()}`);
    },
    shipToHomeOrderLinkTarget: (search: string = '') => {
        // Preserve Shopify's raw query string; the API accepts several possible
        // key names because admin_link and UI extension payloads differ.
        const query = search.startsWith('?') ? search.substring(1) : search;
        return requests.get<AtdShipToHomeOrderLinkTarget>(`/atd/ship-to-home/orders/link-target${query ? `?${query}` : ''}`);
    },
    shipToHomeOrder: (id: number) => requests.get<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}`),
    retryShipToHomeOrder: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/retry`, {}),
    submitShipToHomeOrder: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/submit`, {}),
    changeShipToHomeOrderFulfillmentType: (id: number, fulfillmentType: string) =>
        requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/fulfillment-type`, { fulfillmentType }),
    syncShipToHomeOrderStatus: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/sync-status`, {}),
    archiveShipToHomeOrder: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/archive`, {}),
    unarchiveShipToHomeOrder: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/unarchive`, {}),
    checkShipToHomeCancelEligibility: (id: number) => requests.post<AtdShipToHomeCancelEligibilityResult>(`/atd/ship-to-home/orders/${id}/cancel-eligibility`, {}),
    cancelShipToHomeOrder: (id: number) => requests.post<AtdShipToHomeOrderDetail>(`/atd/ship-to-home/orders/${id}/cancel`, {}),
    processRequestedShipToHomeOrders: () => requests.post<AtdShipToHomeProcessResult>('/atd/ship-to-home/process-requested', {}),
    syncSubmittedShipToHomeStatuses: () => requests.post<AtdShipToHomeStatusSyncResult>('/atd/ship-to-home/sync-status', {}),
    checkoutRatesStatus: () => requests.get<AtdCheckoutCarrierServiceStatus>('/atd/checkout-rates/status'),
    ensureCheckoutRates: () => requests.post<AtdCheckoutCarrierServiceStatus>('/atd/checkout-rates/ensure', {}),
    diagnostics: () => requests.get<AtdDiagnostics>('/atd/diagnostics'),
    productSyncSummary: () => requests.get<AtdProductSyncSummary>('/atd/product-sync'),
    archiveProductSyncStragglers: (payload: AtdProductSyncCleanupRequest) =>
        requests.post<AtdProductSyncCleanupResult>('/atd/product-sync/stragglers/archive', payload),
    products: (
        page: number = 1,
        query: string = '',
        status: string = 'all',
        linkStatus: string = 'all',
        sort: string = 'partnumber asc',
    ) => {
        const params = new URLSearchParams({
            page: String(page),
            query,
            status,
            linkStatus,
            sort,
        });

        return requests.get<AtdProductListResponse>(`/atd/products?${params.toString()}`);
    },
    product: (id: number) => requests.get<AtdProductDetail>(`/atd/products/${id}`),
    shopifyProductSearch: (query: string = '') => {
        const params = new URLSearchParams({ query });
        return requests.get<AtdShopifyProductSearchItem[]>(`/atd/shopify-products?${params.toString()}`);
    },
    saveProductShopifyLink: (id: number, payload: AtdProductShopifyLinkRequest) =>
        requests.put<AtdProductDetail>(`/atd/products/${id}/shopify-link`, payload),
    saveProductManualLinks: (payload: AtdManualProductLinkRequest) =>
        requests.post<AtdManualProductLinkResult>('/atd/products/manual-links', payload),
    previewProductSearch: (query: string) => {
        const params = new URLSearchParams({ query });
        return requests.get<AtdPreviewProductSearchItem[]>(`/atd/preview-products?${params.toString()}`);
    },
    diagnosticProductLookup: (payload: AtdProductLookupDiagnosticRequest) => requests.post<AtdDiagnosticActionResult>('/atd/diagnostics/product-lookup', payload),
    diagnosticStatusLookup: (payload: AtdStatusLookupDiagnosticRequest) => requests.post<AtdDiagnosticActionResult>('/atd/diagnostics/status-lookup', payload),
    diagnosticPreview: (payload: AtdPreviewDiagnosticRequest) => requests.post<AtdDiagnosticActionResult>('/atd/diagnostics/preview', payload),
}

export type ImportFormat = 'csv_xlsx' | 'collection_mappings_csv_xlsx' | 'configurator_data_csv_xlsx' | 'aces_xml' | 'atd_price_file' | 'product_tags' | 'fitment_export';
export type AtdPriceAdjustmentMode = 'none' | 'percent' | 'fixed';
export type ProductTagFormatPreset = 'app_default' | 'turn14' | 'make_model_year' | 'make_model_submodel_year' | 'year_make_model' | 'custom';
export type ProductTagValidationMode = 'create_missing' | 'existing_only';
export type CsvXlsxImportMode = 'append' | 'replace_matched_skus';
export type ConfiguratorAssignmentMode = 'update_only' | 'update_and_assign';
export type PreviewScope = 'aces' | 'atd';
export type ImportHistoryStatus = 'active' | 'archived' | 'all';

export type UploadProgressHandler = (event: AxiosProgressEvent) => void;

export interface AtdPriceImportOptions {
    priceAdjustmentMode: AtdPriceAdjustmentMode;
    priceAdjustmentValue?: number;
    skipZeroMapPrices: boolean;
}

export interface ProductTagImportOptions {
    productTagFormatPreset: ProductTagFormatPreset;
    productTagCustomFormat?: string;
    productTagValidationMode: ProductTagValidationMode;
}

export interface CsvXlsxImportOptions {
    csvXlsxImportMode: CsvXlsxImportMode;
}

export interface ConfiguratorDataImportOptions {
    productGuideId: number;
    configuratorAssignmentMode: ConfiguratorAssignmentMode;
}

export interface ProductTagImportPreviewRow {
    id: number;
    productId: number;
    productTitle: string;
    productVendor: string;
    productSku: string;
    sourceTag: string;
    year: string;
    make: string;
    model: string;
    submodel: string;
    isUniversal: boolean;
    previousPartFinderIsUniversal?: boolean;
    matchedExistingRecord: boolean;
    willCreateFitmentRecord: boolean;
    willApply: boolean;
    isApplied: boolean;
}

export interface ProductTagImportPreview {
    queueId: number;
    awaitingApproval: boolean;
    isApplied: boolean;
    totalRows: number;
    productCount: number;
    fitmentRows: number;
    universalRows: number;
    willApplyRows: number;
    willCreateFitmentRecords: number;
    skippedRows: number;
    rows: ProductTagImportPreviewRow[];
}

export interface ProductTagApproveResult {
    queueId: number;
    appliedFitmentCount: number;
    universalProductCount: number;
}

export interface ProductTagUndoResult {
    queueId: number;
    removedFitmentMappings: number;
    deactivatedFitmentRecords: number;
    universalProductsCleared: number;
}

export interface CsvXlsxImportMappedColumn {
    role: string;
    fieldKey: string;
    fieldLabel: string;
    required: boolean;
    columnIndex: number;
    columnName: string;
}

export interface CsvXlsxImportPreviewRow {
    rowNumber: number;
    sku: string;
    productId: string;
    productHandle: string;
    cells: string[];
    missingRequiredValues: boolean;
    emptyFitment: boolean;
    invalidYearRange?: boolean;
    yearRangeError?: string;
    expandedYearCount?: number;
}

export interface CsvXlsxImportPreview {
    fileName: string;
    canImport: boolean;
    errors: string[];
    warnings: string[];
    headings: string[];
    mappedColumns: CsvXlsxImportMappedColumn[];
    rows: CsvXlsxImportPreviewRow[];
    scannedDataRowCount: number;
    hasMoreRows: boolean;
}

export interface CollectionMappingImportPreviewRow {
    rowNumber: number;
    cells: string[];
    missingCollectionIdentifier: boolean;
    missingRequiredValues: boolean;
    emptyFitment: boolean;
    invalidYearRange: boolean;
}

export interface CollectionMappingImportPreview {
    fileName: string;
    canImport: boolean;
    errors: string[];
    warnings: string[];
    headings: string[];
    mappedColumns: CsvXlsxImportMappedColumn[];
    rows: CollectionMappingImportPreviewRow[];
    scannedDataRowCount: number;
    hasMoreRows: boolean;
}

export interface FitmentImportHistoryItem {
    queueId: number;
    originalFileName: string;
    storedFileName: string;
    importFormat: ImportFormat | 'unknown';
    state: string;
    taggingStatus?: string;
    processingStage?: string;
    processingMessage?: string;
    progressPercent?: number;
    taggingProcessedCount?: number;
    taggingTotalCount?: number;
    taggingUpdatedCount?: number;
    taggingPhase?: string;
    awaitingProductTagApproval: boolean;
    productTagReviewStatus?: string;
    productTagPreviewTotalCount?: number;
    productTagPreviewProductCount?: number;
    productTagWillApplyCount?: number;
    productTagWillCreateCount?: number;
    productTagSkippedCount?: number;
    error?: string;
    addedOn: string;
    startedAt?: string;
    completedAt?: string;
    attemptCount?: number;
    parsedRecordCount?: number;
    appliedRecordCount?: number;
    declaredRecordCount?: number;
    csvXlsxImportedVehicleRecordCount?: number;
    csvXlsxAppliedAssignmentCount?: number;
    collectionMappingCreatedVehicleRecordCount?: number;
    collectionMappingAddedCount?: number;
    collectionMappingReassignedCount?: number;
    collectionMappingUnchangedCount?: number;
    collectionMappingSkippedRowCount?: number;
    configuratorMatchedVariantCount?: number;
    configuratorUpdatedMetafieldCount?: number;
    configuratorUpdatedVariantCount?: number;
    configuratorAssignedProductCount?: number;
    configuratorSkippedProductCount?: number;
    configuratorFailedWriteCount?: number;
    isArchived: boolean;
    archivedAt?: string;
    restrictToVendor: boolean;
    selectedVendorName?: string;
    vendorSkuSuffixRule?: string;
    normalizeSku?: boolean;
    useVendorSkuSuffixRule: boolean;
    updateSavedVendorSkuSuffixRule: boolean;
    csvXlsxImportMode?: CsvXlsxImportMode;
    configuratorAssignmentMode?: ConfiguratorAssignmentMode;
}
export interface FitmentImportQueueResult {
    queueId: number;
    storedFileName: string;
    originalFileName: string;
    importFormat: ImportFormat;
    acesSetup?: AcesImportSetup;
}

export interface AcesImportSetup {
    company?: string;
    documentTitle?: string;
    brandAAIAID?: string;
    submissionType?: string;
    effectiveDate?: string;
    transferDate?: string;
    vendorOptions: string[];
    matchedVendor?: string;
    vendorSkuSuffixRule?: string;
    normalizeSku?: boolean;
}


export interface AdminUpdateDataFeedProfilePayload {
    name?: string;
    providerCode?: string;
    protocol?: string;
    host?: string;
    port: number;
    username?: string;
    password?: string;
    remotePath?: string;
    filePattern?: string;
    isPassive: boolean;
    isEnabled: boolean;
    treatAsFullSnapshot: boolean;
    vendorColumnName?: string;
    defaultVendor?: string;
    isSingleVendorProfile: boolean;
    skuColumnName?: string;
    quantityColumnName?: string;
    delimiter?: string;
    treatBlankQuantityAsZero: boolean;
    quantityBooleanMode?: string;
    shopifyLocationId?: number;
    mapToLocationIfMissing: boolean;
    normalizeShopifySkuSpaces: boolean;
    stripQuantitySuffixPlus: boolean;
    removeSpecialCharactersFromShopifySku: boolean;
    syncIntervalMinutes: number;
    nextRunAt?: string;
    runOnStartup: boolean;
}

export type AdminCreateDataFeedProfilePayload = AdminUpdateDataFeedProfilePayload;

export interface AdminUpsertDataFeedVendorRulePayload {
    shopifyVendor?: string;
    feedVendor?: string;
    matchMode?: string;
    shopifySkuPrefix?: string;
    shopifyLocationId?: number;
    mapToLocationIfMissing: boolean;
    isTurn14Backed: boolean;
}

export interface ConfigureAcesImportPayload {
    selectedVendor?: string;
    skuSuffixRule?: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
}

export interface PreviewSession {
    email: string;
    shopifyStoreId: number;
    scope: PreviewScope;
}

export interface PreviewAtdProductSummary {
    id: number;
    partNumber?: string;
    manufacturerPartNumber?: string;
    productName?: string;
    brandName?: string;
    categoryName?: string;
    isActive: boolean;
    isDiscontinued: boolean;
    availableQuantity: number;
    primaryImageUrl?: string;
    lastCatalogSyncAt?: string;
}


const Admin = {
    originalAppRouting: (token: string) => axios.get<{ enabled: boolean; ready: boolean; blockers: string[] }>('/pf-console/system/original-app-routing', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    setOriginalAppRouting: (token: string, enabled: boolean) => axios.put<{ enabled: boolean; ready: boolean; blockers: string[] }>('/pf-console/system/original-app-routing', { enabled }, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    privacyRequests: (token: string, status: string, page: number) =>
        axios.get<{ items: import('../../features/admin/AdminPrivacyRequestsPage').PrivacyRequestRow[]; totalCount: number }>(`/pf-console/privacy-requests?status=${status}&page=${page}`, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    downloadPrivacyRequest: (token: string, id: string) =>
        axios.post<Blob>(`/pf-console/privacy-requests/${id}/download`, {}, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }).then(responseBody),
    deliverPrivacyRequest: (token: string, id: string) =>
        axios.post(`/pf-console/privacy-requests/${id}/delivered`, { reviewedAndDelivered: true }, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    reviewPrivacyDeletion: (token: string, id: string) =>
        axios.get<{ shopDomain: string; customerId?: string; customerEmail?: string; scope: string; externalReview: string }>(`/pf-console/privacy-requests/${id}/deletion-review`, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    queuePrivacyDeletion: (token: string, id: string, shopDomain: string, verifiedUninstalled: boolean) =>
        axios.post(`/pf-console/privacy-requests/${id}/delete`, { shopDomain, externalCleanupConfirmed: true, verifiedUninstalled }, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    targetPreparation: (token: string, sourceId: number) => axios.get<{ operation: import('../models/Admin').LegacyTargetPreparation | null }>(`/pf-console/migration/sources/${sourceId}/target-preparation`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    prepareLegacyTarget: (token: string, sourceId: number, confirmation: string) => axios.post<import('../models/Admin').LegacyTargetPreparation>(`/pf-console/migration/sources/${sourceId}/target-preparation`, { confirmation }, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    legacyBackendPilot: (token: string, sourceId: number) => axios.get<import('../models/Admin').LegacyBackendPilot>(`/pf-console/migration/sources/${sourceId}/backend-pilot`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    updateLegacyBackendPilot: (token: string, sourceId: number, request: { expectedRevision: number; owner: import('../models/Admin').LegacyBackendOwner; confirmation: string }) =>
        axios.post<import('../models/Admin').LegacyBackendPilot>(`/pf-console/migration/sources/${sourceId}/backend-pilot`, request, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    startPilotImpersonation: (token: string, id: number, native = false) => axios.post<{ handoffUrl: string }>(`/pf-console/${native ? 'stores' : 'migration/sources'}/${id}/impersonation`, {}, {
        headers: { Authorization: `Bearer ${token}` },
    }).then(responseBody),
    pilotImpersonationAudit: (token: string, id: number, native = false) => axios.get<import('../../features/admin/PilotOperatorTools').PilotOperatorAudit>(`/pf-console/${native ? 'stores' : 'migration/sources'}/${id}/impersonation/audit`, {
        headers: { Authorization: `Bearer ${token}` },
    }).then(responseBody),
    migrationStores: (token: string) => axios.get<import('../models/Admin').MigrationStoreDirectory>('/pf-console/migration/stores', {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    refreshLegacyStores: (token: string) => axios.post<import('../models/Admin').LegacyStoreRefreshJob>('/pf-console/migration/refresh-pfv1', {}, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    legacyRefreshJobs: (token: string) => axios.get<import('../models/Admin').LegacyStoreRefreshJob[]>('/pf-console/migration/refresh-pfv1/jobs', {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    legacyRefreshJob: (token: string, jobId: string) => axios.get<import('../models/Admin').LegacyStoreRefreshJob>(`/pf-console/migration/refresh-pfv1/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    cancelLegacyRefreshJob: (token: string, jobId: string) => axios.post<import('../models/Admin').LegacyStoreRefreshJob>(`/pf-console/migration/refresh-pfv1/jobs/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    retryLegacyRefreshJob: (token: string, jobId: string) => axios.post<import('../models/Admin').LegacyStoreRefreshJob>(`/pf-console/migration/refresh-pfv1/jobs/${jobId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    legacyStore: (token: string, sourceId: number) => axios.get<import('../models/Admin').LegacyStoreInventory>(`/pf-console/migration/sources/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    storeReview: (token: string, id: number, legacySource = false) =>
        axios.get<import('../models/Admin').AdminStoreReview>(
            legacySource ? `/pf-console/migration/sources/${id}/review` : `/pf-console/stores/${id}/review`,
            { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    saveStoreReview: (token: string, id: number, legacySource: boolean, input: import('../models/Admin').AdminStoreReview) =>
        axios.put<import('../models/Admin').AdminStoreReview>(
            legacySource ? `/pf-console/migration/sources/${id}/review` : `/pf-console/stores/${id}/review`, input,
            { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    verifyLegacySubscription: (token: string, sourceId: number) => axios.post<import('../models/Admin').LegacyStoreInventory>(`/pf-console/migration/sources/${sourceId}/verify-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    sourceGarageOperations: (token: string, sourceId: number) => axios.get<import('../models/Admin').AdminLegacyGarageOperation[]>(`/pf-console/migration/sources/${sourceId}/garage-operations`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(responseBody),
    queueSourceGarageOperation: (token: string, sourceId: number, operationType: "Preflight" | "StageBaseline", captureConfirmed: boolean, timeoutMinutes: number) =>
        axios.post<import('../models/Admin').AdminLegacyGarageOperation>(`/pf-console/migration/sources/${sourceId}/garage-operations`, {
            operationType, captureConfirmed, timeoutMinutes
        }, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    legacyGarageOperations: (token: string, shopId: number) =>
        axios.get<import('../models/Admin').AdminLegacyGarageOperation[]>(`/pf-console/stores/${shopId}/legacy-garage-operations`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    queueLegacyGarageOperation: (token: string, shopId: number, operationType: "Preflight" | "StageBaseline", captureConfirmed: boolean, timeoutMinutes: number) =>
        axios.post<import('../models/Admin').AdminLegacyGarageOperation>(`/pf-console/stores/${shopId}/legacy-garage-operations`, {
            operationType, captureConfirmed, timeoutMinutes
        }, { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    login: (email: string, password: string) => requests.post<AdminLoginResponse>('/pf-console/login', { email, password }),
    stores: (token: string, query: string = '', page: number = 1, pageSize: number = 50, isActive?: boolean) => {
        const params = new URLSearchParams({
            query,
            page: String(page),
            pageSize: String(pageSize)
        });

        if (isActive !== undefined) {
            params.set('isActive', String(isActive));
        }

        return axios.get<AdminPagedResult<AdminStoreSummary>>(`/pf-console/stores?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    auditHistory: (token: string, shopId: number, filters: Record<string, string> = {}) =>
        axios.get<import('../../features/admin/StoreAuditHistory').StoreAuditResult>(`/pf-console/stores/${shopId}/audit-history`, {
            params: filters, headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    storeDetails: (token: string, shopId: number) =>
        axios.get<AdminStoreDetails>(`/pf-console/stores/${shopId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    shopRuntime: (token: string, shopId: number, useExtendedAccessToken: boolean = true) =>
        axios.get<AdminShopRuntime>(`/pf-console/stores/${shopId}/shopify-runtime?useExtendedAccessToken=${String(useExtendedAccessToken)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    webhookAudit: (token: string, shopId: number) =>
        axios.get<AdminWebhookAudit>(`/pf-console/stores/${shopId}/webhook-audit`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    repairWebhookAudit: (token: string, shopId: number) =>
        axios.post<AdminWebhookAudit>(`/pf-console/stores/${shopId}/webhook-audit/repair`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    deleteWebhookSubscription: (token: string, shopId: number, id: string) =>
        axios.delete<void>(`/pf-console/stores/${shopId}/webhook-subscriptions?id=${encodeURIComponent(id)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    syncLogs: (token: string, page: number = 1, pageSize: number = 50) =>
        axios.get<AdminPagedResult<AdminStoreSyncLog>>(`/pf-console/sync-logs?page=${page}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    getSyncMaintenance: (token: string) =>
        axios.get<import('../models/Admin').SyncServiceMaintenanceStatus>('/pf-console/sync-maintenance', {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    setSyncMaintenance: (token: string, paused: boolean) =>
        axios.post<import('../models/Admin').SyncServiceMaintenanceStatus>('/pf-console/sync-maintenance', { paused }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    syncLogErrors: (token: string, syncLogId: number) =>
        axios.get<AdminSyncLogErrorsResponse>(`/pf-console/sync-logs/${syncLogId}/errors`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    syncHealth: (token: string, lookbackDays: number = 90) =>
        axios.get<AdminSyncHealthDashboard>(`/pf-console/sync-health?lookbackDays=${lookbackDays}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    activity: (token: string, days: number = 30, shopId?: number) => {
        const query = new URLSearchParams({ days: String(days) });
        if (shopId !== undefined) query.set('shopId', String(shopId));

        return axios.get<AdminActivityDashboard>(`/pf-console/activity?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    deleteSyncLog: (token: string, syncLogId: number) =>
        axios.delete<void>(`/pf-console/sync-logs/${syncLogId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    errorLogs: (token: string, page: number = 1, pageSize: number = 50) =>
        axios.get<AdminPagedResult<AdminStoreSyncError>>(`/pf-console/error-logs?page=${page}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    webhookLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, topic?: string, processedSuccessfully?: boolean) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (topic) query.set('topic', topic);
        if (processedSuccessfully !== undefined) query.set('processedSuccessfully', String(processedSuccessfully));

        return axios.get<AdminPagedResult<AdminWebhookLog>>(`/pf-console/webhook-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    dataFeedLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, status?: string) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (status) query.set('status', status);

        return axios.get<AdminPagedResult<AdminDataFeedRunLog>>(`/pf-console/data-feed-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    dataFeedStores: (token: string) =>
        axios.get<AdminDataFeedStoreSummary[]>(`/pf-console/data-feed/stores`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    dataFeedStoreDetails: (token: string, shopId: number) =>
        axios.get<AdminDataFeedStoreDetails>(`/pf-console/data-feed/stores/${shopId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    dataFeedInventoryDiagnostics: (token: string, shopId: number, sku: string, vendor?: string, profileId?: number) => {
        const params = new URLSearchParams({ sku });
        if (vendor) params.set('vendor', vendor);
        if (profileId !== undefined) params.set('profileId', String(profileId));

        return axios.get<AdminDataFeedInventoryDiagnosticsResponse>(`/pf-console/data-feed/stores/${shopId}/inventory-diagnostics?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    updateDataFeedProfile: (token: string, profileId: number, payload: AdminUpdateDataFeedProfilePayload) =>
        axios.put<AdminDataFeedProfile>(`/pf-console/data-feed/profiles/${profileId}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    createDataFeedProfile: (token: string, shopId: number, payload: AdminCreateDataFeedProfilePayload) =>
        axios.post<AdminDataFeedProfile>(`/pf-console/data-feed/stores/${shopId}/profiles`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    runDataFeedProfileNow: (token: string, profileId: number) =>
        axios.post<AdminDataFeedProfile>(`/pf-console/data-feed/profiles/${profileId}/run-now`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    downloadDataFeedProfileDryRunMatchesCsv: (token: string, profileId: number) =>
        axios.get<Blob>(`/pf-console/data-feed/profiles/${profileId}/dry-run-matches.csv`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        }),
    createDataFeedVendorRule: (token: string, profileId: number, payload: AdminUpsertDataFeedVendorRulePayload) =>
        axios.post<AdminDataFeedVendorRule>(`/pf-console/data-feed/profiles/${profileId}/vendor-rules`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateDataFeedVendorRule: (token: string, ruleId: number, payload: AdminUpsertDataFeedVendorRulePayload) =>
        axios.put<AdminDataFeedVendorRule>(`/pf-console/data-feed/vendor-rules/${ruleId}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    importDataFeedVendorRules: (token: string, profileId: number, file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return axios.post<AdminDataFeedVendorRuleImportResult>(`/pf-console/data-feed/profiles/${profileId}/vendor-rules/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    deleteDataFeedVendorRule: (token: string, ruleId: number) =>
        axios.delete<void>(`/pf-console/data-feed/vendor-rules/${ruleId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    fitmentImportLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, state?: string) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (state) query.set('state', state);

        return axios.get<AdminPagedResult<AdminFitmentImportLog>>(`/pf-console/fitment-import-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    clearErrorLogs: (token: string) =>
        axios.delete<void>(`/pf-console/error-logs`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreActiveStatus: (token: string, shopId: number, isActive: boolean) =>
        axios.put<AdminStoreSummary>(`/pf-console/stores/${shopId}/isactive`, { isActive }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreSyncSetting: (token: string, shopId: number, key: string, value: string) =>
        axios.put<AdminStoreSummary>(`/pf-console/stores/${shopId}/sync-settings`, { key, value }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreGeneralSettings: (token: string, shopId: number, payload: AdminStoreGeneralSettings) =>
        axios.put<AdminStoreGeneralSettings>(`/pf-console/stores/${shopId}/general-settings`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreShopifyAppCredentials: (token: string, shopId: number, payload: AdminStoreShopifyAppCredentials) =>
        axios.put<AdminStoreShopifyAppCredentials>(`/pf-console/stores/${shopId}/shopify-app-credentials`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    shopifyAppInstallMappings: (token: string) =>
        axios.get<AdminShopifyAppInstallMapping[]>(`/pf-console/shopify-app-install-mappings`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    upsertShopifyAppInstallMapping: (token: string, payload: AdminShopifyAppInstallMapping) =>
        axios.post<AdminShopifyAppInstallMapping>(`/pf-console/shopify-app-install-mappings`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    deleteShopifyAppInstallMapping: (token: string, id: number) =>
        axios.delete<void>(`/pf-console/shopify-app-install-mappings/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreAtdProfile: (token: string, shopId: number, payload: AdminAtdProfile) =>
        axios.put<AdminAtdProfile>(`/pf-console/stores/${shopId}/atd-profile`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    testStoreAtdProfile: (token: string, shopId: number, payload: AdminAtdProfile) =>
        axios.post<AdminAtdConnectionTestResult>(`/pf-console/stores/${shopId}/atd-profile/test`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    testStoreAtdProfileProducts: (token: string, shopId: number, payload: AdminAtdProfile) =>
        axios.post<AdminAtdFilteredProductSample[]>(`/pf-console/stores/${shopId}/atd-profile/test-products`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    createStoreAtdTestProduct: (token: string, shopId: number, partNumber: string, atdApiProfileId?: number) =>
        axios.post<AdminAtdCreateTestProductResponse>(`/pf-console/stores/${shopId}/atd-profile/create-test-product`, {
            partNumber,
            atdApiProfileId
        }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    clearStoreSurgeMode: (token: string, shopId: number) =>
        axios.post<AdminStoreSummary>(`/pf-console/stores/${shopId}/clear-surge-mode`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    downloadStoreFitmentImportFile: (token: string, shopId: number, queueId: number) =>
        axios.get<Blob>(`/pf-console/stores/${shopId}/fitment-imports/${queueId}/file`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        }),
    deleteStoreFitmentImportHistoryItem: (token: string, shopId: number, queueId: number) =>
        axios.delete<void>(`/pf-console/stores/${shopId}/fitment-imports/${queueId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    restartStoreFitmentImportHistoryItem: (token: string, shopId: number, queueId: number) =>
        axios.post<void>(`/pf-console/stores/${shopId}/fitment-imports/${queueId}/restart`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),

    turn14Items: (token: string, page: number = 1, pageSize: number = 50, query?: string) => {
        const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (query && query.trim().length > 0) params.set('query', query.trim());

        return axios.get<AdminPagedResult<AdminTurn14ItemSummary>>(`/pf-console/turn14/items?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    turn14ItemDetails: (token: string, productId: string) =>
        axios.get<AdminTurn14ItemDetails>(`/pf-console/turn14/items/${encodeURIComponent(productId)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    turn14SyncLogs: (token: string, page: number = 1, pageSize: number = 50) =>
        axios.get<AdminPagedResult<AdminTurn14SyncLog>>(`/pf-console/turn14/sync-logs?page=${page}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    addStoreNote: (token: string, shopId: number, note: string) =>
        axios.post<AdminStoreNote>(`/pf-console/stores/${shopId}/notes`, { note }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
}


const Preview = {
    login: (email: string, password: string) => requests.post<AdminLoginResponse>('/pf-preview/login', { email, password }),
    session: (token: string) => axios.get<PreviewSession>('/pf-preview/session', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
}

const PreviewImports = {
    history: (token: string) => axios.get<FitmentImportHistoryItem[]>('/pf-preview/fitment-imports', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    archive: (token: string, queueId: number) => axios.post<void>(`/pf-preview/fitment-imports/${queueId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } }),
    queue: (token: string, file: File, onUploadProgress?: UploadProgressHandler) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('importFormat', 'aces_xml');

        return axios.post<FitmentImportQueueResult>('/pf-preview/fitment-imports', formData, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            onUploadProgress
        }).then(responseBody);
    },
    configureAces: (token: string, queueId: number, payload: ConfigureAcesImportPayload) => axios.post<void>(`/pf-preview/fitment-imports/${queueId}/aces-setup`, payload, { headers: { Authorization: `Bearer ${token}` } }),
    download: (token: string, queueId: number) => axios.get<Blob>(`/pf-preview/fitment-imports/${queueId}/file`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }),
    vendorSkuRules: (token: string) => axios.get<VendorSkuRule[]>('/settings/vendor-sku-rules', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
}

const PreviewAtd = {
    products: (token: string, page: number = 1, pageSize: number = 50, filters?: { query?: string; brand?: string; isActive?: boolean; inStock?: boolean }) => {
        const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (filters?.query && filters.query.trim()) params.set('query', filters.query.trim());
        if (filters?.brand && filters.brand.trim()) params.set('brand', filters.brand.trim());
        if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
        if (filters?.inStock !== undefined) params.set('inStock', String(filters.inStock));

        return axios.get<AdminPagedResult<PreviewAtdProductSummary>>(`/pf-preview/atd-products?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    }
}

const Imports = {
    lastSuccessful: () => requests.get<{ importFormat: ImportFormat | null }>('/fitmentimports/last-successful'),
    history: (status: ImportHistoryStatus = 'active', options?: {
        kind?: 'imports' | 'exports' | 'spreadsheets'; offset?: number; limit?: number;
    }) => {
        const params = new URLSearchParams({ status });
        if (options?.kind) params.set('kind', options.kind);
        if (options?.offset !== undefined) params.set('offset', String(options.offset));
        if (options?.limit !== undefined) params.set('limit', String(options.limit));
        return requests.get<FitmentImportHistoryItem[]>(`/fitmentimports?${params.toString()}`);
    },
    archive: (queueId: number) => axios.post<void>(`/fitmentimports/${queueId}/archive`, {}),
    productTagsPreview: (queueId: number) => requests.get<ProductTagImportPreview>(`/fitmentimports/${queueId}/product-tags-preview`),
    approveProductTags: (queueId: number) => requests.post<ProductTagApproveResult>(`/fitmentimports/${queueId}/product-tags-approve`, {}),
    undoProductTags: (queueId: number) => requests.post<ProductTagUndoResult>(`/fitmentimports/${queueId}/product-tags-undo`, {}),
    template: () => axios.get<Blob>('/fitmentimports/template', { responseType: 'blob' }),
    collectionMappingTemplate: () => axios.get<Blob>('/fitmentimports/collection-mappings/template', { responseType: 'blob' }),
    configuratorDataTemplate: (productGuideId: number, shopifyCollectionId?: number) => {
        const params = new URLSearchParams({ productGuideId: String(productGuideId) });
        if (shopifyCollectionId) params.set('shopifyCollectionId', String(shopifyCollectionId));
        return axios.get<Blob>(`/fitmentimports/configurator-data/template?${params.toString()}`, { responseType: 'blob' });
    },
    activeExport: () => requests.get<boolean>('/fitmentimports/export/active'),
    importCapacity: () => requests.get<{ activeCount: number; limit: number }>('/fitmentimports/capacity'),
    exportFitments: () => {
        const formData = new FormData();
        formData.append('importFormat', 'fitment_export');

        // Use the existing queue endpoint so exports follow the same API path as
        // imports; the worker decides the job behavior from importFormat.
        return axios.post<FitmentImportQueueResult>('/fitmentimports', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(responseBody);
    },
    previewCsvXlsx: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return axios.post<CsvXlsxImportPreview>('/fitmentimports/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(responseBody);
    },
    previewCollectionMappings: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return axios.post<CollectionMappingImportPreview>('/fitmentimports/collection-mappings/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(responseBody);
    },
    previewConfiguratorData: (file: File, productGuideId: number) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('productGuideId', String(productGuideId));

        return axios.post<CsvXlsxImportPreview>('/fitmentimports/configurator-data/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(responseBody);
    },
    queue: (file: File | undefined, importFormat: ImportFormat, onUploadProgress?: UploadProgressHandler, atdPriceOptions?: AtdPriceImportOptions, productTagOptions?: ProductTagImportOptions, csvXlsxOptions?: CsvXlsxImportOptions, configuratorOptions?: ConfiguratorDataImportOptions) => {
        const formData = new FormData();
        if (file) {
            formData.append('file', file);
        }
        formData.append('importFormat', importFormat);

        if (importFormat === 'csv_xlsx' && csvXlsxOptions) {
            formData.append('csvXlsxImportMode', csvXlsxOptions.csvXlsxImportMode);
        }

        if (importFormat === 'configurator_data_csv_xlsx' && configuratorOptions) {
            formData.append('productGuideId', String(configuratorOptions.productGuideId));
            formData.append('configuratorAssignmentMode', configuratorOptions.configuratorAssignmentMode);
        }

        if (importFormat === 'atd_price_file' && atdPriceOptions) {
            formData.append('priceAdjustmentMode', atdPriceOptions.priceAdjustmentMode);
            formData.append('skipZeroMapPrices', String(atdPriceOptions.skipZeroMapPrices));

            if (typeof atdPriceOptions.priceAdjustmentValue === 'number') {
                formData.append('priceAdjustmentValue', String(atdPriceOptions.priceAdjustmentValue));
            }
        }

        if (importFormat === 'product_tags' && productTagOptions) {
            formData.append('productTagFormatPreset', productTagOptions.productTagFormatPreset);
            formData.append('productTagValidationMode', productTagOptions.productTagValidationMode);
            if (productTagOptions.productTagCustomFormat) {
                formData.append('productTagCustomFormat', productTagOptions.productTagCustomFormat);
            }
        }

        return axios.post<FitmentImportQueueResult>('/fitmentimports', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress
        }).then(responseBody);
    },
    configureAces: (queueId: number, payload: ConfigureAcesImportPayload) => axios.post<void>(`/fitmentimports/${queueId}/aces-setup`, payload),
    download: (queueId: number) => axios.get<Blob>(`/fitmentimports/${queueId}/file`, { responseType: 'blob' }),
    downloadReview: (queueId: number) => axios.get<Blob>(`/fitmentimports/${queueId}/review-file`, { responseType: 'blob' })
}

const Reports = {
    searchActivity: (options?: {
        days?: number;
        from?: string;
        to?: string;
        comparison?: string;
        comparisonFrom?: string;
        comparisonTo?: string;
        completedDaysOnly?: boolean;
        limit?: number;
    }) => {
        const params = new URLSearchParams();
        params.set('days', String(options?.days ?? 30));
        params.set('limit', String(options?.limit ?? 100));
        params.set('comparison', options?.comparison ?? 'previous_period');
        if (options?.completedDaysOnly) {
            params.set('completedDaysOnly', 'true');
        }
        if (options?.from && options?.to) {
            params.set('from', options.from);
            params.set('to', options.to);
        }
        if (options?.comparisonFrom && options?.comparisonTo) {
            params.set('comparisonFrom', options.comparisonFrom);
            params.set('comparisonTo', options.comparisonTo);
        }

        return requests.get<StorefrontSearchReport>(`/reports?${params.toString()}`);
    },
    coverageSettings: () => requests.get<CoverageReportSettings>('/reports/coverage-settings'),
    updateCoverageSettings: (settings: CoverageReportSettings) =>
        requests.put<CoverageReportSettings>('/reports/coverage-settings', settings),
    fitmentCoverage: (limit: number = 250, lowCoverageThreshold?: number, show: CoverageFilter = 'all') =>
        requests.get<FitmentCoverageHealthReport>(`/reports/fitment-coverage?limit=${limit}&show=${show}${lowCoverageThreshold === undefined ? '' : `&lowCoverageThreshold=${lowCoverageThreshold}`}`),
}

const agent = {
    Support: {
        submit: (ticket: SupportTicketRequest) => requests.post<SupportTicketResult>('/support/tickets', ticket),
    },
    Account,
    Settings,
    Groups,
    ProductGuides,
    ServiceBookings,
    ShopifyMetafieldAttributes,
    SyncLogInfo,
    PricingInfo,
    Products,
    CollectionMappings,
    Vehicles,
    Atd,
    Admin,
    Preview,
    PreviewImports,
    PreviewAtd,
    Imports,
    Reports,
    LegacyAdmin
}
 
export default agent;
